import type { DrawingDocument, DrawingEntity, QualityIssue } from '../contract/types.js';
import { escapeXml as esc, formatNumber as f } from './format.js';
import { shapeMarkup } from './shapes.js';

/**
 * The visual overlay.
 *
 * Generated directly from the structured data by templating, with no drawing
 * library. Two reasons: the output stays byte-deterministic, which is what
 * makes its content hash meaningful; and a rendering library would introduce
 * version-dependent output for no benefit — the shapes here are lines, circles,
 * arcs and text boxes over a page.
 *
 * Coordinates stay in the original PDF points and every mark carries its entity
 * identifier, so anything a reviewer clicks can be traced back to the entity
 * that produced it. Entities the system could not support are drawn too, never
 * silently dropped.
 */

const STYLE = [
  '.d2d-entity{fill:none;stroke:#0b7285;stroke-width:0.7;vector-effect:non-scaling-stroke}',
  '.d2d-entity.d2d-text{fill:#0b7285;stroke:none}',
  '.d2d-issue{fill:none;stroke:#d9480f;stroke-width:1;stroke-dasharray:4 2;vector-effect:non-scaling-stroke}',
].join('');

export interface OverlayOptions {
  /**
   * Draw into a larger frame than this page occupies, without rescaling.
   *
   * A comparison shows the same drawing three ways — before, after and the
   * difference — and they only register if all three share one coordinate
   * system. When the two versions' sheets differ in size, that system is the
   * union of both, and the smaller sheet sits inside it at its own size.
   * Scaling it to fit instead would turn a change of paper into apparent
   * movement of every entity on the sheet.
   */
  frame?: { widthPt: number; heightPt: number };
}

export function renderOverlay(
  doc: DrawingDocument,
  pageIndex: number,
  options: OverlayOptions = {},
): string {
  const page = doc.pages.find((p) => p.index === pageIndex);
  if (!page) throw new Error(`no such page: ${pageIndex}`);
  const frame = options.frame ?? { widthPt: page.widthPt, heightPt: page.heightPt };

  const parts: string[] = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${f(frame.widthPt)} ${f(frame.heightPt)}"` +
      ` width="${f(frame.widthPt)}" height="${f(frame.heightPt)}"` +
      ` data-contract-version="${esc(doc.contractVersion)}" data-page="${page.index}"` +
      ` data-scale-state="${esc(doc.scaleState)}">`,
    `<style>${STYLE}</style>`,
    '<g class="d2d-entities">',
  ];

  for (const e of page.entities) parts.push(renderEntity(e));
  parts.push('</g>', '<g class="d2d-issues">');

  for (const q of doc.qualityIssues) {
    if (q.pageIndex === page.index && q.bbox) parts.push(renderIssue(q));
  }

  parts.push('</g>', '</svg>');
  return parts.join('\n');
}

function renderEntity(e: DrawingEntity): string {
  const attrs = ` data-entity-id="${esc(e.id)}" data-entity-type="${e.type}" data-layer="${esc(e.layer)}"`;
  return shapeMarkup(e, 'd2d-entity', 'd2d-entity d2d-text', attrs);
}

function renderIssue(q: QualityIssue): string {
  const [x0, y0, x1, y1] = q.bbox!;
  // A zero-area box would be invisible; give it a minimum so the reviewer can
  // still see that something was recorded there.
  const w = Math.max(x1 - x0, 0.75);
  const h = Math.max(y1 - y0, 0.75);
  return (
    `<rect class="d2d-issue ${esc(q.kind)}" x="${f(x0)}" y="${f(y0)}"` +
    ` width="${f(w)}" height="${f(h)}" data-issue-kind="${esc(q.kind)}"` +
    ` data-operator="${esc(q.operator ?? '')}" data-count="${q.count}"/>`
  );
}
